/**
 * `/teacher/prep` — one row per lecture and problem session. A prepared one links to
 * its workbench; an unprepared one is plain text. Rows are a list, not a table, so the
 * page reads at 400 px.
 */
import { Link } from "react-router";
import { Badge, Card, EmptyState, PageHeader, Spinner } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatDayShort } from "@client/lib/format";
import { TeacherNav } from "../TeacherNav";
import { usePrepList, type PrepSummary } from "./api";

function PrepRow({ lecture }: { lecture: PrepSummary }) {
  const t = useT();
  const { lang } = useLang();
  const heading = `${lecture.id} ${lecture.title}`;
  return (
    <Card compact as="article">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="font-display text-lg text-ink-900 dark:text-ink-50 [overflow-wrap:anywhere]">
            {lecture.available ? (
              <Link
                to={`/teacher/prep/${encodeURIComponent(lecture.id)}`}
                className="hover:underline"
              >
                {heading}
              </Link>
            ) : (
              heading
            )}
          </h2>
          {lecture.date && (
            <p className="text-sm text-ink-600 dark:text-ink-400">
              {formatDayShort(lecture.date, lang)}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {lecture.kind && <Badge tone="accent">{t(`teacher.prep.kind.${lecture.kind}`)}</Badge>}
          {!lecture.available && <Badge>{t("teacher.prep.notAvailable")}</Badge>}
          {lecture.reviewStatus === "draft" && (
            <Badge tone="warning">{t("teacher.prep.draft")}</Badge>
          )}
        </div>
      </div>
      {lecture.objectOfLearning && (
        <p className="mt-2 text-sm text-ink-700 dark:text-ink-300">{lecture.objectOfLearning}</p>
      )}
    </Card>
  );
}

export function TeacherPrepIndexPage() {
  const t = useT();
  const query = usePrepList();

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title={t("teacher.prep.title")} description={t("teacher.prep.description")} />
      <TeacherNav />

      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}
      {!query.isLoading && !query.data && <EmptyState title={t("common.error")} />}
      {query.data && (
        <ul className="flex flex-col gap-3">
          {query.data.lectures.map((lecture) => (
            <li key={lecture.id}>
              <PrepRow lecture={lecture} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
